import React, { useEffect, useState } from 'react';
import { AlertCircle, Megaphone, Send, UserCheck, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { BriefingDraft } from '../../utils/territorialBriefing';
interface BriefingDraftDialogProps {
  draft: BriefingDraft | null;
  onClose: () => void;
  onSend: () => void;
}

/** Contextualised draft prepared by ATNA, to review and edit before sending. */
export function BriefingDraftDialog({
  draft,
  onClose,
  onSend
}: BriefingDraftDialogProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  useEffect(() => {
    if (!draft) return;
    setSubject(draft.subject[lang]);
    setBody(draft.body[lang]);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [draft, lang, onClose]);
  if (!draft) return null;
  const isFollowup = draft.kind === 'followup';
  const Icon = isFollowup ? UserCheck : Megaphone;
  const canSend = subject.trim().length > 0 && body.trim().length > 0;
  return <div className="fixed inset-0 z-[1000] bg-slate-900/40 flex items-center justify-center p-4" onClick={onClose} data-id="element-5216" data-mp="gy7g4c4boa5p">
      <div role="dialog" aria-modal="true" aria-labelledby="briefing-draft-title" className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col font-body" onClick={e => e.stopPropagation()} data-id="element-5217" data-mp="qv3gtvnzbrzp">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100" data-id="element-5218" data-mp="zsvuywhystli">
          <h2 id="briefing-draft-title" className="text-base font-bold text-[#0F172A] font-heading flex items-center gap-2" data-id="element-5219" data-mp="14o1oagdp5ajm">
            <Icon className="w-4 h-4 text-[#4F46E5]" data-id="element-5220" data-mp="1elfvdpdqcmz9" />
            {isFollowup ? t('Demande de suivi', 'Follow-up request') : t('Information citoyenne', 'Citizen information')}
          </h2>
          <button onClick={onClose} aria-label={t('Fermer', 'Close')} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-id="element-5221" data-mp="3fmm3xkepgsi4">
            <X className="w-4 h-4" data-id="element-5222" data-mp="3ofz6le2kn8he" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-4" data-id="element-5223" data-mp="3jmwjnosauxa6">
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-id="element-5224" data-mp="27hicttddzzb">
            <AlertCircle className="w-4 h-4 shrink-0 mt-px" data-id="element-5225" data-mp="3tcpkbuqwlrzn" />
            {t('Brouillon préparé par ATNA à partir du briefing. Relisez-le et ajustez-le avant l’envoi.', 'Draft prepared by ATNA from the briefing. Review and adjust it before sending.')}
          </div>

          <div data-id="element-5226" data-mp="2d0ahag1uhxdf">
            <div className="text-xs font-medium text-slate-500 mb-1" data-id="element-5227" data-mp="2czspbvm7u5bb">{t('Destinataire', 'Recipient')}</div>
            <div className="text-sm text-[#0F172A] px-3 py-2 rounded-lg bg-slate-50 border border-slate-200" data-id="element-5228" data-mp="28zqo1nke7dwk">
              {draft.to[lang]}
            </div>
          </div>

          <label className="block" data-id="element-5229" data-mp="22uanw3u3gjbj">
            <span className="text-xs font-medium text-slate-500" data-id="element-5230" data-mp="22xez3l5z8fbp">{t('Objet', 'Subject')}</span>
            <input value={subject} onChange={e => setSubject(e.target.value)} className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/30 focus:border-[#4F46E5]" data-id="element-5231" data-mp="1p231n60fsar3" />
          </label>

          <label className="block" data-id="element-5232" data-mp="165bcify7r1f4">
            <span className="text-xs font-medium text-slate-500" data-id="element-5233" data-mp="166365l892p2r">{t('Message', 'Message')}</span>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={11} className="mt-1 w-full px-3 py-2 text-sm leading-relaxed border border-slate-200 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/30 focus:border-[#4F46E5]" data-id="element-5234" data-mp="1b3mooxbzdo0i" />
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100" data-id="element-5235" data-mp="10h39k0gzes7c">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50" data-id="element-5236" data-mp="vi04ig104lzd">
            {t('Annuler', 'Cancel')}
          </button>
          <button onClick={onSend} disabled={!canSend} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] hover:bg-[#4338CA] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5237" data-mp="midculy2quji">
            <Send className="w-4 h-4" data-id="element-5238" data-mp="7sxofcm0n16z" />
            {isFollowup ? t('Envoyer la demande', 'Send request') : t('Transmettre aux Communications', 'Send to Communications')}
          </button>
        </div>
      </div>
    </div>;
}